import { isValidBulkCarryoverDateValue, isValidCarryoverDateValue, isValidUndoCarryoverDateValue } from "./ValidationHelper.tsx";

import moment from "moment-timezone";
/*
  Date Behavior Notes
  -----------------
  Dates are stored as generic dates/datetimes in the form of epochs in our DB. They do not have any associated/expected timezone.

  All dates should be saved using the epoch time, only compare dates using UTC time, and all dates should be displayed in the user's local timezone.
   This is to avoid any issues with users in different timezones seeing different date values or having issues with date comparisons.

   The below assumption about dates created from string using the user's timezone is incorrect,
   as timezones for dates created from ISO strings are converted differently that generic date formats (like MM/DD/YYYY)


 // previous dev notes in case we need them for context:
  Dates created off of epochs like from our DB are automatically converted to the user's local timezone from GMT.
  This can place them in a different day unless we adjust for timezone through GetDateObjectFromEpoch() below
  
  Dates created off of strings assume the datetime is in the user's timezone and do not require conversion

  Dates created off of other Date objects simply inherit the Date object's data.
*/


/** 
 * Returns the current date with time, converted between UTC timezone
 * @returns Return DateTime adjusted by UTC timezone offset
 */
export function getSaveableDateWithTime() {
  return new Date().getTime();
}

export function getUTCMoment(date: Date | number) {
  if (typeof date === "number") {
    return moment(date).utc()
  }

  return moment(date.getTime()).utc();

}
/**
 * Given a date, returns either its fiscal year or fiscal year quarter.
 * 
 * TODO:
 * * We should combine the object and string methods to just create a new date to avoid formatting variations with strings
 * @param value - Date to examine
 * @param isYear - Whether to return the Fiscal Year or Fiscal Year Quarter
 * @returns Either a yyyy Fiscal Year or QX quarter value depending on isYear
 */
export function calculateFiscalYearQuarter(value, isYear) {
  //Fiscal Quarters are Q1 = 10/1 to 12/31, Q2 = 1/1 to 3/31, Q3 = 4/1 to 6/31, Q4 = 7/1 to 9/31
  let month;
  let year;
  if (value === null) {
    return "";
  } else if (value && typeof (value) === 'object') {
    month = value.getMonth() + 1;
    year = value.getFullYear();
  } else if (value && typeof (value) === 'string') {
    if (value.includes("-")) {
      //Value comes in as YYYY-MM-DD;
      const dateSplit = value.split("-");
      month = parseInt(dateSplit[1]);
      year = parseInt(dateSplit[0]);
    } else if (value.includes("/")) {
      //Value comes in as MM/DD/YYYY;
      const dateSplit = value.split("/");
      month = parseInt(dateSplit[0]);
      year = parseInt(dateSplit[2]);
    }
  } else if(value && typeof (value) === 'number')
  {
    let stringVal = formatDateForDisplay(value);
    const dateSplit = stringVal.split("/");
    month = parseInt(dateSplit[0]);
    year = parseInt(dateSplit[2]);
  }
  else {
    return "";
  }

  //Default state is for the current year fiscal year. The current year and fiscal year match
  let fiscalYear = year;
  let fiscalQuarter = "";
  if (month >= 10 && month <= 12) {
    fiscalYear = year + 1;
    fiscalQuarter = "Q1";
  } else if (month >= 1 && month <= 3) {
    fiscalQuarter = "Q2";
  } else if (month >= 4 && month <= 6) {
    fiscalQuarter = "Q3";
  } else if (month >= 7 && month <= 9) {
    fiscalQuarter = "Q4";
  }
  if (isYear) {
    return fiscalYear;
  } else {
    return fiscalQuarter;
  }

}

/**
 * Given a fiscal year, return the starting date of that fiscal year
 * 
 * @param year - Target fiscal year as a Number
 * @returns Starting date of the current fiscal year (October 1st)
 */
export function getStartDateOfTargetFiscalYear(fiscalYear) {
  let fiscalYearVal = fiscalYear?.FiscalYear ?? fiscalYear;
  let startOfFiscalYear = `10/01/${fiscalYearVal-1}`;
  return startOfFiscalYear;
}

/**
 * Given a fiscal year, return the end date of that fiscal year
 * 
 * @param year - Target fiscal year as a Number
 * @returns Starting date of the current fiscal year (September 30th)
 */
export function getEndDateOfTargetFiscalYear(fiscalYear) {
  let fiscalYearVal = fiscalYear?.FiscalYear ?? fiscalYear;
  let endOfFiscalYear = `09/30/${fiscalYearVal}`;
  return endOfFiscalYear;
}


export function compareLocalDatesFromIsoString(iso, compareToDate) {
      if (!iso) return false;
      const overrideDate = moment(iso); // convert
      // local current date
      return overrideDate.isSameOrAfter(moment(compareToDate), 'day'); // compare
}


/** 
 * gets epoch time from imput date
 */
export function getSaveableDate(date: Date | string | number | null): number | null {
  if (date && typeof (date) === 'object') {
    return date.getTime();
  } else if (date && typeof (date) === 'string') {
    if (date.includes("-")) {
      return moment(date).valueOf();
    } else if (date.includes("/")) {
      // assume UTC time as that is how it was previously saved
      const dateSplit = date.split("/");
      return moment.utc({
        year: parseInt(dateSplit[2]),
        month: parseInt(dateSplit[0]) - 1,
        day: parseInt(dateSplit[1])
      }).valueOf();
    } else {
      return null;
    }
  } else if (date && typeof (date) === 'number') {
    return date;
  } else {
    return null;
  }
}


/** 
return date in MM-DD-YYYY format given a date value in string or epoch format Local Timezone adjusted
 */
export function formatDateForDisplay(dateValue: string | number | Date, format = "MM/DD/YYYY"): string {
  if (dateValue && ['number', 'object', 'string'].includes( typeof dateValue)) {
      return moment(dateValue).format(format);
  } else {
    return '';
  }
}


/** 
 * Given a date string, format it in a mmddyyyy string used for locating download files
 * 
 * TODO:
 * * After improvements are made to formatDateForDisplay(), replace calls to this method with appropriate calls to that one
 * @param dateString - Date to examine in string format
 * @returns A date string formatted as mmddyyyy
 */
export function formatDateForDownloadFiles(dateObj) {
  if (dateObj && typeof (dateObj) === 'object') {
    let month = dateObj.getMonth() + 1;
    let finalMonth = month;
    if (month < 10) {
      let monthStr = month.toString();
      finalMonth = "0" + monthStr;
    }
    let day = dateObj.getDate();
    let finalDay = day;
    if (day < 10) {
      let dayStr = day.toString();
      finalDay = "0" + dayStr;
    }
    let year = dateObj.getFullYear()?.toString().substring(2);
    return `${finalMonth}${finalDay}${year}`;
  }
  else {
    return null;
  }
}

/** 
 * Get a specific day prior to the current date
 * @param daysPrior - Number of days in the past
 * @returns Returns a date string corresponding to {daysPrior} days to the current date
 */
export const getDaysBeforeDate = (daysPrior) => {
  return (d => new Date(d.setDate(d.getDate() - daysPrior)))(new Date());

};


/** 
 * Given a date object, format it as a astring at the beginning of the day 
 * @param date - Date object
 * @returns Return yyyy-mm-dd date string with time appended based on the start of the day
 */
export const getStartOfDay = (date: number) => {
      date = new Date(date);
      if(!isNaN(date)){
        const d = new Date(date)
        const month = d.getUTCMonth() + 1;
        const day = d.getUTCDate();
        const year = d.getUTCFullYear();

        return `${year}-${month}-${day} 00:00:00`;
      } else {
        return '';
      }
    }

/** 
 * Given a date object, format it as a string at the end of the day 
 * @param date - Date object
 * @returns Return yyyy-mm-dd date string with time appended based on the end of the day
 */
export const getEndOfDay = (date) => {
  date = new Date(date);
  if(!isNaN(date)){
    const month = date.getUTCMonth() + 1;
    const day = date.getUTCDate();
    const year = date.getUTCFullYear();

    return `${year}-${month}-${day} 23:59:59`;
  } else {
    return ''
  }
}

export const handleCarryoverTargetInitDateChange = (value, setTargetInitiationDateValue, setCurrentError, setToShowError, setCarryoverTargetFiscalYearSelection, feature) => {
    if(value === null){
      setTargetInitiationDateValue(null);
      setCurrentError("");
      setToShowError(false);
    } else {
      if(!isNaN(Date.parse(value))){
        setCurrentError("");
        setToShowError(false);

        const dateErrString = isValidCarryoverDateValue(value, feature);
        if(!dateErrString)
        {
          setTargetInitiationDateValue(value);
          let targetFY = calculateFiscalYearQuarter(value, true);
          setCarryoverTargetFiscalYearSelection({ FiscalYear: `${targetFY}` });
        }
        else
        {
          setCurrentError(dateErrString);
          setToShowError(true);
        }
      } else {
        setCurrentError("Invalid Date Format");
        setToShowError(true);
      }
    }
    
  }

  export const handleUndoCarryoverTargetInitDateChange = (value, setTargetInitiationDateValue, setCurrentError, setToShowError, setCarryoverTargetFiscalYearSelection) => {
    if(value === null){
      setTargetInitiationDateValue(null);
      setCurrentError("");
      setToShowError(false);
    } else {
      if(!isNaN(Date.parse(value))){
        setCurrentError("");
        setToShowError(false);

        const dateErrString = isValidUndoCarryoverDateValue(value);
        if(!dateErrString)
        {
          setTargetInitiationDateValue(value);
          let targetFY = calculateFiscalYearQuarter(value, true);
          setCarryoverTargetFiscalYearSelection({ FiscalYear: `${targetFY}` });
        }
        else
        {
          setCurrentError(dateErrString);
          setToShowError(true);
        }
      } else {
        setCurrentError("Invalid Date Format");
        setToShowError(true);
      }
    }
    
  }

  export const handleBulkCarryoverTargetInitDateChange = (value, setTargetInitiationDateValue, setCurrentError, setToShowError, setCarryoverTargetFiscalYearSelection, allSelectedItems) => {
    if(value === null){
      setTargetInitiationDateValue(null);
      setCurrentError("");
      setToShowError(false);
    } else {
      if(!isNaN(Date.parse(value))){
        setCurrentError("");
        setToShowError(false);

        const dateErrString = isValidBulkCarryoverDateValue(value, allSelectedItems).join(", ");
        if(!dateErrString)
        {
          setTargetInitiationDateValue(value);
          let targetFY = calculateFiscalYearQuarter(value, true);
          setCarryoverTargetFiscalYearSelection({ FiscalYear: `${targetFY}` });
        }
        else
        {
          setCurrentError(dateErrString);
          setToShowError(true);
        }
      } else {
        setCurrentError("Invalid Date Format");
        setToShowError(true);
      }
    }
    
  }


export const checkYearIsFourDigits = (value: Date | null | number): string => {
  return value ? (getUTCMoment(value).year().toString().length !== 4 ? "The date year must be 4 digits." : "") : "";
}

export const getFiscalYear = (date: Date | number): number => {
  // use this cause it handles both Date and epoch number
    const d = getUTCMoment(date);
    const year  = d.year();
    const month = d.month();
    return (month >= 9) ? year + 1 : year;
}


export const getStartOrEndOfFiscalYear = (fiscalYear: number, isStart: boolean): Date => {
    if(isStart){
        return new Date(Date.UTC(fiscalYear - 1, 9, 1, 0, 0, 0)); // October 1st of previous year
    }
    return new Date(Date.UTC(fiscalYear, 8, 30, 23, 59, 59)); // September 30th of current fiscal year
}


export const sortFeatureAttributesByDate = (features: any[], dateFieldName: string, isAsc: boolean): any[] => {
  return features.sort((a, b) => {
    let firstItem = getSaveableDate(a[dateFieldName]);
    let secondItem = getSaveableDate(b[dateFieldName]);

    firstItem = firstItem || -Infinity;
    secondItem = secondItem || -Infinity;
    return isAsc ? firstItem - secondItem : secondItem - firstItem;
  });
}